import { computed, resolveDynamicComponent } from 'vue';

import { propsFactory, warn } from '@components/utils';

import type { ComputedRef, PropType } from 'vue';
import type { RouteLocationRaw, RouterLink as TRouterLink, useLink as TUseLink, UseLinkOptions } from 'vue-router';

export interface ILinkProps {
  href: string | undefined;
  to: RouteLocationRaw | undefined;
  disabled?: boolean;
}
export interface IUseLink extends Omit<Partial<ReturnType<typeof TUseLink>>, 'href' | 'isActive'> {
  isActive: ComputedRef<boolean> | undefined;
  isLink: ComputedRef<boolean>;
  href: ComputedRef<string | undefined>;
  to: ComputedRef<RouteLocationRaw | undefined>;
  tag: ComputedRef;
  handleClick: (event: MouseEvent) => void;
}

export const makeLinkProps = propsFactory(
  {
    /**
     * Обычная ссылка
     */
    href: {
      type: String as PropType<string>,
      default: undefined
    },
    /**
     * Ссылка или объект Vue Router
     */
    to: {
      type: [String, Object] as PropType<RouteLocationRaw>,
      default: undefined
    }
  },
  'link'
);

export function useLink(props: ILinkProps, emit: Function): IUseLink {
  if (props.to && props.href) {
    warn('link', 'You cannot use both property to and href');
  }

  const RouterLink = resolveDynamicComponent('RouterLink') as typeof TRouterLink;
  const isRouterLink = computed(() => !!props.to);
  const isLink = computed(() => !!(props.href || props.to));

  const link = isRouterLink.value ? RouterLink.useLink(props as UseLinkOptions) : undefined;
  const to = computed(() => (isRouterLink.value ? props.to : undefined));
  const href = computed(() => (isRouterLink.value ? link?.route.value.href : props.href));
  const isActive = link && computed(() => link.isActive?.value);
  const tag = computed(() => (isRouterLink.value ? RouterLink : 'a'));

  const handleClick = (event: MouseEvent): void => {
    emit('click', event);

    if (props.disabled) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
  };

  return {
    isLink,
    isActive,
    route: link?.route,
    navigate: link?.navigate,
    to,
    href,
    tag,
    handleClick
  };
}
